import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import Badge from '../../components/Badge';
import { UsersIcon, TicketIcon, PaymentIcon, CheckInIcon, SeatIcon, EventIcon } from '../../components/Icons';

const fmt = (d) => d ? new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–';

const STATS = [
  {
    key: 'total_users',
    label: 'Benutzer',
    Icon: UsersIcon,
    gradient: 'from-reef-500 to-reef-700',
    glow: 'shadow-reef-200',
    valueColor: 'text-reef-700',
    bg: 'bg-reef-50',
  },
  {
    key: 'total_tickets',
    label: 'Tickets verkauft',
    Icon: TicketIcon,
    gradient: 'from-ocean-400 to-ocean-700',
    glow: 'shadow-ocean-200',
    valueColor: 'text-ocean-700',
    bg: 'bg-ocean-50',
  },
  {
    key: 'open_payments',
    label: 'Offene Zahlungen',
    Icon: PaymentIcon,
    gradient: 'from-sand-400 to-sand-600',
    glow: 'shadow-sand-200',
    valueColor: 'text-sand-700',
    bg: 'bg-sand-50',
  },
  {
    key: 'checked_in',
    label: 'Eingecheckt',
    Icon: CheckInIcon,
    gradient: 'from-emerald-400 to-emerald-600',
    glow: 'shadow-emerald-200',
    valueColor: 'text-emerald-700',
    bg: 'bg-emerald-50',
  },
  {
    key: 'seats_booked',
    label: 'Sitzplätze belegt',
    Icon: SeatIcon,
    gradient: 'from-ocean-500 to-reef-600',
    glow: 'shadow-ocean-200',
    valueColor: 'text-ocean-700',
    bg: 'bg-ocean-50',
  },
  {
    key: 'total_events',
    label: 'Events',
    Icon: EventIcon,
    gradient: 'from-slate-500 to-ocean-800',
    glow: 'shadow-slate-200',
    valueColor: 'text-slate-700',
    bg: 'bg-slate-50',
  },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/api/admin/dashboard').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <h2 className="page-title">Dashboard</h2>

      <div className="card mb-4">
        {stats === null ? (
          <p className="text-sm text-slate-400">Lade aktuelles Event…</p>
        ) : stats.current_event ? (
          <div className="flex flex-wrap gap-3 justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500 text-white">
                  Aktuelles Event
                </span>
                <span className="font-bold text-ocean-900">{stats.current_event.name}</span>
                <Badge status={stats.current_event.status} />
              </div>
              <p className="text-xs text-slate-500">
                {stats.current_event.location || '–'} · {fmt(stats.current_event.start_date)} – {fmt(stats.current_event.end_date)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {stats.current_event.tickets_sold || 0}/{stats.current_event.max_participants || '∞'} Tickets · {stats.current_event.seats_booked || 0} Sitze belegt
              </p>
            </div>
            <Link to="/admin/events" className="text-xs font-semibold text-ocean-600 hover:text-ocean-800 no-underline self-center">
              Events verwalten →
            </Link>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 justify-between items-center">
            <p className="text-sm text-slate-500">Kein aktives Event.</p>
            <Link to="/admin/events" className="text-xs font-semibold text-ocean-600 hover:text-ocean-800 no-underline">
              Event aktivieren →
            </Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {STATS.map(({ key, label, Icon, gradient, glow, valueColor, bg }) => (
          <div
            key={key}
            className="card relative overflow-hidden flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-4 text-center sm:text-left p-4 group hover:shadow-card-hover transition-shadow duration-200"
          >
            {/* Subtle background wave decoration */}
            <div className="absolute -right-3 -bottom-3 w-16 h-16 opacity-[0.04] pointer-events-none">
              <svg viewBox="0 0 64 64" fill="currentColor" className="text-ocean-900">
                <path d="M0 40c8-8 16-8 24 0s16 8 24 0s16-8 24 0v24H0V40z"/>
              </svg>
            </div>

            {/* Icon badge with gradient */}
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-sm shadow-${glow} group-hover:scale-105 transition-transform duration-200`}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>

            <div className="relative">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide leading-tight">{label}</p>
              <p className={`text-2xl sm:text-3xl font-extrabold tabular-nums ${valueColor}`}>
                {stats?.[key] ?? <span className="opacity-30">–</span>}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
